/**
 * Changelog Parser
 *
 * Parses CHANGELOG.md into structured data for display in the app.
 * Maintains the "single source of truth" principle - edit CHANGELOG.md,
 * and the app automatically reflects the changes.
 */

export interface ChangelogFeature {
  title: string; // e.g., "Visitor Tracking"
  description: string; // e.g., "Cloudflare statistics"
  bullets: string[]; // Sub-bullet points
}

export interface ChangelogSection {
  added: ChangelogFeature[];
  changed: ChangelogFeature[];
  fixed: ChangelogFeature[];
}

export interface ChangelogVersion {
  version: string; // e.g., "0.1.1-alpha"
  date: string; // e.g., "2025-12-23"
  sections: ChangelogSection;
}

export interface ParsedChangelog {
  versions: ChangelogVersion[];
  futurePlans: string[]; // Planned features from "Future Plans" section
}

/**
 * Parse CHANGELOG.md content into structured data
 */
export function parseChangelog(markdownContent: string): ParsedChangelog {
  const lines = markdownContent.split('\n');
  const versions: ChangelogVersion[] = [];
  const futurePlans: string[] = [];

  let currentVersion: ChangelogVersion | null = null;
  let currentSection: 'added' | 'changed' | 'fixed' | null = null;
  let currentFeature: ChangelogFeature | null = null;
  let inFuturePlans = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // Skip empty lines
    if (!trimmedLine) {
      // Empty line ends current feature
      if (currentFeature && currentVersion && currentSection) {
        currentVersion.sections[currentSection].push(currentFeature);
        currentFeature = null;
      }
      continue;
    }

    // Match version header: ## [0.1.1-alpha] - 2025-12-23
    const versionMatch = trimmedLine.match(/^##\s+\[([^\]]+)\]\s+-\s+(.+)$/);
    if (versionMatch) {
      // Save previous feature if exists
      if (currentFeature && currentVersion && currentSection) {
        currentVersion.sections[currentSection].push(currentFeature);
        currentFeature = null;
      }
      // Save previous version if exists
      if (currentVersion) {
        versions.push(currentVersion);
      }

      // Start new version
      currentVersion = {
        version: versionMatch[1].trim(),
        date: versionMatch[2].trim(),
        sections: {
          added: [],
          changed: [],
          fixed: [],
        },
      };
      currentSection = null;
      inFuturePlans = false;
      continue;
    }

    // Match "Future Plans" section
    if (trimmedLine.match(/^##\s+Future Plans/i)) {
      // Save previous feature if exists
      if (currentFeature && currentVersion && currentSection) {
        currentVersion.sections[currentSection].push(currentFeature);
        currentFeature = null;
      }
      // Save previous version if exists
      if (currentVersion) {
        versions.push(currentVersion);
        currentVersion = null;
      }

      inFuturePlans = true;
      currentSection = null;
      continue;
    }

    // Match section headers: ### Added, ### Changed, ### Fixed
    const sectionMatch = trimmedLine.match(/^###\s+(Added|Changed|Fixed|Planned Features)/i);
    if (sectionMatch) {
      // Save previous feature if exists
      if (currentFeature && currentVersion && currentSection) {
        currentVersion.sections[currentSection].push(currentFeature);
        currentFeature = null;
      }

      const sectionName = sectionMatch[1].toLowerCase();
      if (sectionName === 'added' || sectionName === 'changed' || sectionName === 'fixed') {
        currentSection = sectionName as 'added' | 'changed' | 'fixed';
      }
      continue;
    }

    // Parse feature items
    if (trimmedLine.startsWith('- ')) {
      const content = trimmedLine.substring(2).trim();

      // Check if this is a sub-bullet (indented bullet)
      if (line.startsWith('  - ') && currentFeature) {
        // This is a sub-bullet of the current feature
        const subBullet = content;
        currentFeature.bullets.push(subBullet);
        continue;
      }

      // Save previous feature if exists
      if (currentFeature && currentVersion && currentSection) {
        currentVersion.sections[currentSection].push(currentFeature);
      }

      // Handle Future Plans items (simple bullets without bold titles)
      if (inFuturePlans) {
        futurePlans.push(content);
        currentFeature = null;
        continue;
      }

      // Parse feature item with **Title** - Description format
      const featureMatch = content.match(/^\*\*([^*]+)\*\*\s*-?\s*(.*)$/);
      if (featureMatch) {
        // Feature with bold title
        currentFeature = {
          title: featureMatch[1].trim(),
          description: featureMatch[2].trim(),
          bullets: [],
        };
      } else {
        // Feature without bold title (e.g., in Fixed section)
        currentFeature = {
          title: '',
          description: content,
          bullets: [],
        };
      }
    }
  }

  // Save final feature and version
  if (currentFeature && currentVersion && currentSection) {
    currentVersion.sections[currentSection].push(currentFeature);
  }
  if (currentVersion) {
    versions.push(currentVersion);
  }

  return {
    versions,
    futurePlans,
  };
}

/**
 * Get the latest version from parsed changelog
 */
export function getLatestVersion(changelog: ParsedChangelog): ChangelogVersion | null {
  return changelog.versions.length > 0 ? changelog.versions[0] : null;
}

/**
 * Validate that the changelog was parsed successfully
 */
export function isValidChangelog(changelog: ParsedChangelog): boolean {
  return changelog.versions.length > 0;
}
